import { Button, Select, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '../../../providers/AuthProvider';
import { useUserDevices } from '../../../hooks/useUserDevices';
import { usePatients } from '../../../hooks/usePatients';
import { createConfiguration } from '../../../services/configurationManager';
import { getAppConfig } from '../../../configs/apps/appConfigs';
import { scenarioMaps } from '../../../data/maps';
import { DeviceFields } from './shared/DeviceFields';
import { DEV_TOOLS_COMBOBOX_PROPS } from './shared/comboboxProps';
import { ExposureQuickForm } from './forms/ExposureQuickForm';
import { PerformanceQuickForm } from './forms/PerformanceQuickForm';
import { BodyImageQuickForm } from './forms/BodyImageQuickForm';
import { buildQuickPayload } from './build/buildQuickPayload';
import {
  DEFAULT_EXPOSURE_CATEGORIES,
  type ExposureQuickFormState,
  type PerformanceQuickFormState,
  type QuickConfigAppType,
} from './types';

const APP_OPTIONS: { value: QuickConfigAppType; label: string }[] = [
  { value: 'exposure', label: 'Exposure' },
  { value: 'performance', label: 'Performance' },
  { value: 'body_image', label: 'Body Image' },
];

function defaultExposureState(): ExposureQuickFormState {
  const map = scenarioMaps.exposure?.[0]?.value ?? 'restaurant';
  return {
    map,
    userSpawnPosition: '1',
    hasTutorial: false,
    multiItem: false,
    categories: { ...DEFAULT_EXPOSURE_CATEGORIES },
  };
}

function defaultPerformanceState(): PerformanceQuickFormState {
  const config = getAppConfig('performance');
  return {
    map: scenarioMaps.performance?.[0]?.value ?? 'volcano',
    currentState: config.defaultLevel ?? 'p-2',
    variation: config.defaultVariation ?? 5,
    duration: config.defaultDuration ?? 300,
  };
}

type QuickConfigTabProps = {
  /** Prefill from current session when available; otherwise pick from list. */
  patientId?: string | number | null;
};

export function QuickConfigTab({ patientId }: QuickConfigTabProps) {
  const navigate = useNavigate();
  const user = useAuthUser();
  const { devices } = useUserDevices(user?.id?.toString());
  const { patients, loading: patientsLoading } = usePatients();

  const [appType, setAppType] = useState<QuickConfigAppType>('exposure');
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [sensorId, setSensorId] = useState('');
  const [exposure, setExposure] = useState(defaultExposureState);
  const [performance, setPerformance] = useState(defaultPerformanceState);
  const [isCreating, setIsCreating] = useState(false);

  const sessionPatientId = useMemo(
    () => (patientId != null && patientId !== '' ? String(patientId) : ''),
    [patientId]
  );

  useEffect(() => {
    if (sessionPatientId) {
      setSelectedPatientId(sessionPatientId);
    }
  }, [sessionPatientId]);

  const patientIdStr = selectedPatientId || sessionPatientId;
  const requireSensor = Boolean(getAppConfig(appType).devices?.sensor);

  const patientOptions = useMemo(
    () =>
      patients.map(p => ({
        value: p.value,
        label: p.label,
      })),
    [patients]
  );

  const handleCreate = async () => {
    try {
      setIsCreating(true);
      const payload = buildQuickPayload({
        appType,
        patientId: patientIdStr,
        devices: {
          deviceId,
          sensorId: sensorId || undefined,
        },
        exposure: appType === 'exposure' ? exposure : undefined,
        performance: appType === 'performance' ? performance : undefined,
        bodyImage: appType === 'body_image' ? {} : undefined,
      });

      const response = await createConfiguration(payload);
      const newId = response.data?.id;
      notifications.show({
        color: 'green',
        title: 'Sessione test creata',
        message: newId
          ? `Configurazione #${newId} creata`
          : 'Configurazione creata',
      });
      if (newId != null) {
        navigate(`/sessions/${newId}`);
      }
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Errore',
        message:
          error instanceof Error
            ? error.message
            : 'Impossibile creare la sessione test',
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Stack gap='sm'>
      <Select
        label='Paziente'
        placeholder={patientsLoading ? 'Caricamento…' : 'Seleziona paziente'}
        data={patientOptions}
        value={patientIdStr || null}
        onChange={v => setSelectedPatientId(v ?? '')}
        searchable
        required
        comboboxProps={DEV_TOOLS_COMBOBOX_PROPS}
      />
      <Select
        label='App'
        data={APP_OPTIONS}
        value={appType}
        onChange={v => setAppType((v as QuickConfigAppType) ?? 'exposure')}
        comboboxProps={DEV_TOOLS_COMBOBOX_PROPS}
      />
      <DeviceFields
        devices={devices}
        deviceId={deviceId}
        sensorId={sensorId}
        onDeviceIdChange={setDeviceId}
        onSensorIdChange={setSensorId}
        requireSensor={requireSensor}
      />
      {appType === 'exposure' && (
        <ExposureQuickForm value={exposure} onChange={setExposure} />
      )}
      {appType === 'performance' && (
        <PerformanceQuickForm value={performance} onChange={setPerformance} />
      )}
      {appType === 'body_image' && <BodyImageQuickForm />}
      <Button
        size='sm'
        loading={isCreating}
        disabled={!patientIdStr || !deviceId}
        onClick={() => void handleCreate()}
      >
        Crea sessione test
      </Button>
    </Stack>
  );
}
